import React, { useState, useContext, useEffect } from "react";
import { Temporal } from "@js-temporal/polyfill";

import { Dashboard, Delivery, DispatchTask } from "./model";
import { useDashboardConfig } from "./dispatch_api_client";
import { CurrentTimeContext } from "./delivery";
import { createDriverNote } from "./driver"
import { adminChatsPath } from "src/routes";

// a DispatchTask, after resolving the deliveryId to a Delivery
interface TaskWithDelivery {
  id: number;
  delivery: Delivery;
  taskType: string;
  createdAt: Temporal.ZonedDateTime;
  readyAt: Temporal.ZonedDateTime;
}

const TaskList = ({
  title,
  count,
  children
} : {
  title: string,
  count: number,
  children: React.ReactNode,
}): React.JSX.Element => {
  if (count == 0) return <></>;
  return <>
    <div className="task-group">
      <div className="subheading">
        <span className="text">{title}</span>
        <span className="count">{count}</span>
      </div>

      <div className="task-list">
        {children}
      </div>
    </div>
  </>;
}

// Each task is a particular situation for a particular Delivery. We don't want to present at that fine a level;
// we roll up to a level that becomes the subject of the dispatcher's required action, for example the driver if
// the dispatcher needs to nudge the driver, or the store if the dispatcher needs to call the store.
function filterAndGroupTasks<K>(
  tasks: TaskWithDelivery[] | undefined,
  callbackFn: (task: TaskWithDelivery) => K | undefined | null,
): Map<K, TaskWithDelivery[]> {
  return (tasks ?? []).reduce(
    (results, task) => {
      const key = callbackFn(task);
      if (!key) return results;

      const values = results.get(key);
      if (values) {
        values.push(task);
      } else {
        results.set(key, [task]);
      }
      return results;
    },
    new Map<K, TaskWithDelivery[]>());
}

function secondsToHHMMSS(seconds: number) {
  const hours = Math.floor(seconds/3600);
  const mins = Math.floor(seconds/60) % 60;
  const secs = Math.floor(seconds) % 60;
  if (hours == 0) {
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  } else {
    return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
}

const TaskCardHeader = ({
  tasks,
  children,
} : {
  tasks: TaskWithDelivery[],
  children: React.ReactNode,
}) => {
  const currentTime = useContext(CurrentTimeContext);
  const firstCreatedAt = tasks.map((task) => task.createdAt).reduce((min, time) => Temporal.ZonedDateTime.compare(time, min) > 0 ? min : time);
  const firstReadyAt = tasks.map((task) => task.readyAt).reduce((min, time) => Temporal.ZonedDateTime.compare(time, min) > 0 ? min : time);
  const elapsedSeconds = (currentTime.getTime() - firstCreatedAt.epochMilliseconds)/1000;
  const readySeconds = (currentTime.getTime() - firstReadyAt.epochMilliseconds)/1000;

  return <>
    <div className="header">
      <div className="heading">
        {children}
      </div>
      <div className={`time ${readySeconds > 300 ? 'unresolved-for-five-minutes' : ''}`}>
        {secondsToHHMMSS(elapsedSeconds)}
      </div>
    </div>
  </>;
}

function pickupTime(delivery: Delivery) {
  const readyForPickup = delivery.status == "Ready For Pick Up";

  return <>
    <div title={readyForPickup ? "Pickup time (ready for pickup!)" : "Pickup time"} className={readyForPickup ? 'pickup-time ready-for-pickup' : 'pickup-time'}>
      <span className="icon is-small has-text-grey">
        <i className="far fa-sm fa-clock"></i>
      </span>
      {delivery.pickupTime.toUpperCase()}
    </div>
  </>;
}

function docketDescription(delivery: Delivery) {
  return <>
    <div className="item">
      <div className="description">
        <a href={delivery.href} title="Open order" target="_blank" rel="noreferrer" className={delivery.operationsException ? 'operations-exception' : ''}>
          <span className="reference">{delivery.docketReference}</span>
          <span className="icon is-small ml-1">
            <i className="fa fa-arrow-up-right-from-square"></i>
          </span>
        </a>
      </div>
      {pickupTime(delivery)}
    </div>
  </>
}

const TaskCardDockets = ({
  tasks,
  suffix,
} : {
  tasks: TaskWithDelivery[],
  suffix: string,
}) => {
  if (tasks.length == 1) {
    return <>
      <div className="task-description is-single">
        {docketDescription(tasks[0].delivery)}
      </div>
    </>;
  } else {
    return <>
      <details className="task-description is-multiple">
        <summary><span className="count">{tasks.length} orders</span> {suffix}</summary>
        {tasks.map((task) => docketDescription(task.delivery))}
      </details>
    </>;
  }
}

function deliveryDescription(delivery: Delivery) {
  return <>
    <div className="item">
      <div className="description">
        <div className="pickup-from">
          {delivery.storeName}
        </div>
        <div className="deliver-to">
          <span className="icon is-small">
            <i className="fas fa-xs fa-arrow-right"></i>
          </span>
          <span className="destination">{delivery.suburb}</span>
        </div>
      </div>
      {pickupTime(delivery)}
    </div>
  </>
}

const TaskCardDeliveries = ({
  tasks,
  suffix,
} : {
  tasks: TaskWithDelivery[],
  suffix: string,
}) => {
  if (tasks.length == 1) {
    return <>
      <details className="task-description is-single">
        <summary>{deliveryDescription(tasks[0].delivery)}</summary>
      </details>
    </>;
  } else {
    return <>
      <details className="task-description is-multiple">
        <summary><span className="count">{tasks.length} deliveries</span> {suffix}</summary>
        {tasks.map((task) => deliveryDescription(task.delivery))}
      </details>
    </>;
  }
}

const CallButton = ({
  phoneNumber,
  children,
} : {
  phoneNumber: string | undefined,
  children: React.ReactNode,
}) => {
  return phoneNumber == undefined || phoneNumber == '' ? (
      <button className="button is-small is-primary" disabled>
        <span className="icon is-small">
          <i className="fas fa-phone"></i>
        </span>
        <span>{children}</span>
      </button>
    ) : (
      <a href={`aircall://${phoneNumber.replaceAll(/\s+/g, "")}`} title={`Call ${phoneNumber}`}>
        <button className="button is-small is-primary">
          <span className="icon is-small">
            <i className="fas fa-phone"></i>
          </span>
          <span>{children}</span>
        </button>
      </a>
    );
}

const StoreTaskList = ({
  title,
  tasksByStore,
  suffix,
} : {
  title: string,
  tasksByStore: Map<number, TaskWithDelivery[]>,
  suffix: string,
}) => {
  if (tasksByStore.size == 0) return <></>;

  return <>
    <TaskList title={title} count={tasksByStore.size}>
      {Array.from(tasksByStore.entries(), ([storeId, storeTasks]) => {
        // stores aren't first-class objects on the dashboard; we basically have to extract them from the fields on Delivery
        // at present the fields of interest here will be the same for all deliveries from the store (unless it literally got
        // edited between delivery broadcasts), so not bothering to check the other tasks.
        const store = { name: storeTasks[0].delivery.storeName, phone: storeTasks[0].delivery.storePhone, href: storeTasks[0].delivery.storeHref };

        return (
          <div key={storeId} className={`task ${storeTasks[0].taskType}`}>
            <TaskCardHeader tasks={storeTasks}>
              {store.name}
            </TaskCardHeader>

            <TaskCardDockets tasks={storeTasks} suffix={suffix} />

            <div className="actions">
              <CallButton phoneNumber={store.phone}>
                Call store
              </CallButton>

              <a href={store.href} title="Open store" target="_blank" rel="noreferrer" className="button is-small is-outlined">
                <span className="icon is-small">
                  <i className="fa fa-arrow-up-right-from-square"></i>
                </span>
              </a>
            </div>
          </div>
        );
      })}
    </TaskList>
  </>;
}

const jumpToDriver = (driverId: number) => {
  const element = document.getElementById(`driver_${driverId}`);
  if (element) {
    element.scrollIntoView(false);
    element.classList.add("pulse");
    const finished = () => {
      element.classList.remove("pulse")
      element.removeEventListener("animationend", finished);
    }
    element.addEventListener("animationend", finished);
  }
}

const DriverTaskList = ({
  title,
  tasksByDriver,
  suffix,
  dashboard,
} : {
  title: string,
  tasksByDriver: Map<number, TaskWithDelivery[]>,
  suffix: string,
  dashboard: Dashboard,
}) => {
  const { apiClient } = useDashboardConfig();

  if (tasksByDriver.size == 0) return <></>;

  return <>
    <TaskList title={title} count={tasksByDriver.size}>
      {Array.from(tasksByDriver.entries(), ([driverId, driverTasks]) => {
        const driver = dashboard.driversById().get(driverId);
        return (
          <div key={driverId} className={`task ${driverTasks[0].taskType}`}>
            <TaskCardHeader tasks={driverTasks}>
              {driver?.name ?? 'Unknown driver!'}
            </TaskCardHeader>

            <TaskCardDeliveries tasks={driverTasks} suffix={suffix} />

            <div className="actions">
              <CallButton phoneNumber={driver?.mobileNumber}>
                Call driver
              </CallButton>

              <a href={`${adminChatsPath()}/dispatcher?area[]=${dashboard.areaSlug}&focus=${driverId}`} title="Chat" target="_blank" rel="noreferrer" className="button is-small is-outlined">
                <span className="icon">
                  <i className="far fa-message"></i>
                </span>
              </a>

              <button onClick={() => createDriverNote(apiClient, driverId)} className="button is-small is-outlined" title="Add driver note">
                <span className="icon is-small">
                  <i className="far fa-file-pen"></i>
                </span>
              </button>

              <button onClick={() => jumpToDriver(driverId)} className="button is-small is-outlined" title="Jump to driver">
                <span className="icon is-small">
                  <i className="fa fa-arrow-down-to-line"></i>
                </span>
              </button>
            </div>
          </div>
        );
      })}
    </TaskList>
  </>;
}

function hydrateTasks(dispatchTasks: DispatchTask[], deliveries: Map<string, Delivery>): TaskWithDelivery[] {
  // To make the broadcasts independent, DispatchTask only refers to deliveries by ID. We need to use the actual
  // Delivery objects, so hydrate the task objects here.
  const tasks: TaskWithDelivery[] = [];

  for (const dispatchTask of dispatchTasks) {
    const delivery = deliveries.get(dispatchTask.deliveryId);
    if (!delivery) continue;

    tasks.push({
      id: dispatchTask.id,
      delivery: delivery,
      taskType: dispatchTask.taskType,
      createdAt: dispatchTask.createdAt,
      readyAt: dispatchTask.readyAt,
    });
  }

  return tasks;
}

function groupTasks(tasks: TaskWithDelivery[]): Map<string, TaskWithDelivery[]> {
  const tasksByType = new Map<string, TaskWithDelivery[]>();

  for (const task of tasks) {
    const tasksForType = tasksByType.get(task.taskType);

    if (tasksForType) {
      tasksForType.push(task);
    } else {
      tasksByType.set(task.taskType, [task]);
    }
  }

  return tasksByType;
}

const TaskStrip = ({
  dashboard,
} : {
  dashboard: Dashboard;
}) => {
  // Dashboard's CurrentTimeProvider has a 5s tick, to reduce the overhead of re-rendering all the bits of the dash that
  // only need low-resolution time changes. We display the actual seconds, so use a 1s tick.
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const intervalTimer = window.setInterval(() => { setNow(new Date()); }, 500);
    return () => { clearInterval(intervalTimer); };
  });

  if (!dashboard.tasks) return <></>;
  const tasksWithDeliveries = hydrateTasks(dashboard.tasks, dashboard.deliveriesById());
  const tasksByType = groupTasks(tasksWithDeliveries);

  const docketNotAcknowledgedTasks = filterAndGroupTasks<number>(tasksByType.get('docket_not_acknowledged'), (task) => task.delivery.storeId);
  const deliveryNotAcceptedTasks = filterAndGroupTasks<number>(tasksByType.get('delivery_not_accepted'), (task) => task.delivery.driverId);
  const driverWaitingAtStoreTasks = filterAndGroupTasks<number>(tasksByType.get('waiting_at_store'), (task) => task.delivery.driverId);

  return <>
    <div className="dispatch-dashboard-tasks">
      <div className="heading">
        <span className="text">Tasks</span>
        <span className="count">{docketNotAcknowledgedTasks.size + deliveryNotAcceptedTasks.size + driverWaitingAtStoreTasks.size}</span>
      </div>
      <div className="task-groups">
        <CurrentTimeContext.Provider value={now}>
          <StoreTaskList title="Store hasn't acknowledged" tasksByStore={docketNotAcknowledgedTasks} suffix="unacknowledged" />
          <DriverTaskList title="Not accepted by driver" tasksByDriver={deliveryNotAcceptedTasks} suffix="not accepted" dashboard={dashboard} />
          <DriverTaskList title="Waiting at store" tasksByDriver={driverWaitingAtStoreTasks} suffix="to collect" dashboard={dashboard} />
        </CurrentTimeContext.Provider>
      </div>
    </div>
  </>;
}

export { TaskStrip };
